import { useEffect, useState } from 'react';
import useSearch from 'hooks/store/useSearch';
import useUserPreferences from 'hooks/useUserPreferences';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { computeQuickPurchaseEligibility } from 'utils/quickPurchasePassengers';
import growthBook from 'services/growthBook';

/**
 * useQuickPurchaseGBAttribute hook.
 * Computes Quick Purchase eligibility from the current search context and sets the
 * `isEligibleForQuickPurchase` attribute on GrowthBook so the experiment can target
 * only eligible users. Reports a `isReady` flag the caller can use to delay rendering
 * consumers of the experiment value until after the attribute has been applied.
 * @param {object} params - Hook parameters.
 * @param {Array} params.trips - Search result trips.
 * @param {boolean} params.isRoundTrip - Whether the search is round trip.
 * @param {boolean} params.isExchange - Whether the flow is an exchange.
 * @param {boolean} params.isSearchPolling - Whether the search is still polling.
 * @param {boolean} [params.noTrips] - Whether the search resolved with zero trips.
 * @returns {{
 *   isReady: boolean,
 *   isEligibleForQuickPurchase: boolean,
 *   isOnlyAdults: boolean,
 *   isWithinPassengerLimit: boolean,
 *   hasEnoughPassengers: boolean,
 *   anyTripAllowsSeatSelection: boolean,
 *   qpPassengers: Array,
 *   passengersSource: string,
 * }}
 */
const useQuickPurchaseGBAttribute = ({ trips, isRoundTrip, isExchange, isSearchPolling }) => {
  const { passengers: searchPassengers } = useSearch();
  const { passengers: frequentPassengers } = useUserPreferences();
  const features = useWhitelabelFeatures();
  const [isReady, setIsReady] = useState(false);

  const eligibility = computeQuickPurchaseEligibility({
    searchPassengersEncoded: searchPassengers?.passengers,
    frequentPassengers,
    features,
    trips,
    isRoundTrip,
    isExchange,
  });

  const { isEligibleForQuickPurchase } = eligibility;

  useEffect(() => {
    if (isSearchPolling) return;
    if (growthBook.isInitialized()) {
      growthBook.setAttributes({
        isEligibleForQuickPurchase,
      });
    }

    setIsReady(true);
  }, [isSearchPolling, isEligibleForQuickPurchase]);

  return {
    isReady,
    isEligibleForQuickPurchase,
    isOnlyAdults: eligibility.isOnlyAdults,
    isWithinPassengerLimit: eligibility.isWithinPassengerLimit,
    hasEnoughPassengers: eligibility.hasEnoughPassengers,
    anyTripAllowsSeatSelection: eligibility.anyTripAllowsSeatSelection,
    qpPassengers: eligibility.qpPassengers,
    passengersSource: eligibility.passengersSource,
  };
};

export default useQuickPurchaseGBAttribute;
